import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  UserPlus, ShieldCheck, Trash2, Crown, AlertCircle, Send, BellRing,
} from "lucide-react";

// Multi-admin management. Only the primary admin (ADMIN_EMAIL) can add or
// remove co-admins. Any admin can view the list — useful so co-admins know
// who else has access.
export default function AdminTeam() {
  const { user, loading } = useAuth();
  const [items, setItems] = useState([]);
  const [primaryEmail, setPrimaryEmail] = useState("");
  const [currentIsPrimary, setCurrentIsPrimary] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [busyEmail, setBusyEmail] = useState("");
  const [granting, setGranting] = useState(false);

  // Verification reminder broadcast — nudges every organizer whose KYC
  // isn't approved yet. Any admin (primary or co-admin) can trigger.
  const [reminderPreview, setReminderPreview] = useState(null);
  const [reminderBusy, setReminderBusy] = useState(false);

  const refresh = useCallback(async () => {
    setFetching(true);
    setFetchError("");
    try {
      const { data } = await api.get("/admin/team");
      setItems(Array.isArray(data.items) ? data.items : []);
      setPrimaryEmail(data.primary_admin_email || "");
      setCurrentIsPrimary(!!data.current_user_is_primary);
    } catch (err) {
      setFetchError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setFetching(false);
    }
  }, []);

  const refreshReminderPreview = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/verify-reminders/preview");
      setReminderPreview(data);
    } catch {
      // Silent — the button will re-fetch on click if this fails.
      setReminderPreview(null);
    }
  }, []);

  useEffect(() => {
    if (user && user.is_admin) {
      refresh();
      refreshReminderPreview();
    }
  }, [user, refresh, refreshReminderPreview]);

  if (loading) return null;
  if (!user || !user.is_admin) return <Navigate to="/" replace />;

  const onGrant = async (e) => {
    e.preventDefault();
    const clean = newEmail.trim().toLowerCase();
    if (!clean) {
      toast.error("Enter an email address.");
      return;
    }
    setGranting(true);
    try {
      const { data } = await api.post("/admin/team/grant", { email: clean });
      if (data.already_admin) {
        toast.info(`${clean} already has admin access.`);
      } else {
        toast.success(`Granted co-admin access to ${clean}`);
      }
      setNewEmail("");
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setGranting(false);
    }
  };

  const onRevoke = async (email) => {
    if (!window.confirm(`Remove admin access for ${email}?`)) return;
    setBusyEmail(email);
    try {
      await api.post("/admin/team/revoke", { email });
      toast.success(`Removed admin access from ${email}`);
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusyEmail("");
    }
  };

  const onSendReminders = async () => {
    // Refresh preview to make sure the count in the confirm is fresh.
    let count = reminderPreview?.would_send;
    try {
      const { data } = await api.get("/admin/verify-reminders/preview");
      setReminderPreview(data);
      count = data.would_send;
    } catch { /* fall through with stale count */ }
    if (!count) {
      toast.info("No organizers need a reminder right now — every eligible one is already verified or was pinged in the last 7 days.");
      return;
    }
    const throttled = reminderPreview?.throttled_recent || 0;
    const msg = throttled
      ? `Send a verification-reminder email to ${count} organizer${count === 1 ? "" : "s"}?\n\n${throttled} more were already emailed in the last 7 days and will be skipped.`
      : `Send a verification-reminder email to ${count} organizer${count === 1 ? "" : "s"}?`;
    if (!window.confirm(msg)) return;
    setReminderBusy(true);
    try {
      const { data } = await api.post("/admin/verify-reminders/send");
      toast.success(
        data.sent
          ? `Sent to ${data.sent} organizer${data.sent === 1 ? "" : "s"}` +
              (data.throttled_recent
                ? ` · ${data.throttled_recent} skipped (recent)` : "")
          : "No emails sent"
      );
      await refreshReminderPreview();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setReminderBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-6 py-16" data-testid="admin-team-page">
      <div className="overline" style={{ color: "var(--terracotta)" }}>Admin · Team</div>
      <h1 className="font-serif text-5xl mt-3">Admin team.</h1>
      <p className="mt-4 text-sm max-w-2xl" style={{ color: "var(--ink-soft)" }}>
        Grant a trusted teammate access to the moderation queue, analytics, and every
        other admin tool — without sharing your login. They&apos;ll need to sign up on
        AddVendTour first with their own email, then you can promote them here.
      </p>

      {currentIsPrimary ? (
        <form
          onSubmit={onGrant}
          data-testid="admin-team-grant-form"
          className="mt-10 rounded-lg border p-6"
          style={{ borderColor: "rgba(0,0,0,0.12)", background: "rgba(111,125,85,0.05)" }}
        >
          <div className="flex items-center gap-2 mb-4">
            <UserPlus size={18} style={{ color: "var(--moss)" }} />
            <h2 className="text-lg font-medium">Invite a co-admin</h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1 w-full">
              <Label htmlFor="grant-email" className="text-xs uppercase tracking-widest">
                Email of existing account
              </Label>
              <Input
                id="grant-email"
                data-testid="admin-team-grant-email"
                type="email"
                placeholder="teammate@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="mt-2"
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="admin-team-grant-submit"
              disabled={granting}
              className="btn-primary py-6 px-8 rounded-full inline-flex items-center gap-2"
            >
              <ShieldCheck size={16} />
              {granting ? "Granting…" : "Grant admin access"}
            </Button>
          </div>
          <p className="mt-3 text-xs" style={{ color: "var(--ink-soft)" }}>
            Co-admins get every admin power except managing the team itself — only
            you (the primary admin) can add or remove co-admins. Keeps the invite
            chain safe from lockouts.
          </p>
        </form>
      ) : (
        <div
          data-testid="admin-team-readonly-notice"
          className="mt-10 rounded-lg border p-5 flex items-start gap-3 text-sm"
          style={{ borderColor: "rgba(0,0,0,0.12)", background: "rgba(0,0,0,0.03)" }}
        >
          <AlertCircle size={18} className="shrink-0 mt-0.5" style={{ color: "var(--ink-soft)" }} />
          <div>
            <div className="font-medium">You&apos;re a co-admin.</div>
            <div className="mt-1" style={{ color: "var(--ink-soft)" }}>
              Only the primary admin ({primaryEmail || "unset"}) can add or remove other
              admins. You can still see who has access below.
            </div>
          </div>
        </div>
      )}

      <div className="mt-12">
        <h2 className="text-lg font-medium mb-4">Current admins</h2>

        {fetching ? (
          <div className="text-sm py-6" style={{ color: "var(--ink-soft)" }}>Loading…</div>
        ) : fetchError ? (
          <div
            data-testid="admin-team-fetch-error"
            className="rounded-md p-3 text-sm"
            style={{ background: "rgba(196,79,54,0.08)", color: "var(--terracotta)" }}
          >
            {fetchError}
          </div>
        ) : (
          <ul
            data-testid="admin-team-list"
            className="divide-y rounded-lg border"
            style={{ borderColor: "rgba(0,0,0,0.12)" }}
          >
            {items.map((m) => (
              <li
                key={m.email}
                data-testid={`admin-team-row-${m.email}`}
                className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {m.is_primary ? (
                    <Crown size={18} style={{ color: "var(--terracotta)" }} />
                  ) : (
                    <ShieldCheck size={18} style={{ color: "var(--moss)" }} />
                  )}
                  <div className="min-w-0">
                    <div className="font-medium truncate">
                      {m.email}
                      {m.is_primary && (
                        <span
                          className="ml-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                          style={{
                            background: "rgba(196,79,54,0.12)",
                            color: "var(--terracotta)",
                          }}
                        >
                          Primary
                        </span>
                      )}
                    </div>
                    <div className="text-xs mt-0.5" style={{ color: "var(--ink-soft)" }}>
                      {m.name || "—"}
                      {m.role ? ` · ${m.role}` : ""}
                      {m.granted_at && !m.is_primary ? (
                        <>
                          {" · granted "}
                          {new Date(m.granted_at).toLocaleDateString()}
                          {m.granted_by ? ` by ${m.granted_by}` : ""}
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>

                {!m.is_primary && currentIsPrimary && (
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`admin-team-revoke-${m.email}`}
                    disabled={busyEmail === m.email}
                    onClick={() => onRevoke(m.email)}
                    className="rounded-full inline-flex items-center gap-2"
                  >
                    <Trash2 size={14} />
                    {busyEmail === m.email ? "Removing…" : "Revoke"}
                  </Button>
                )}
              </li>
            ))}
            {items.length === 0 && (
              <li className="px-5 py-6 text-sm" style={{ color: "var(--ink-soft)" }}>
                No admins yet.
              </li>
            )}
          </ul>
        )}
      </div>

      <div
        data-testid="admin-team-broadcasts"
        className="mt-14 rounded-lg border p-6"
        style={{ borderColor: "rgba(0,0,0,0.12)", background: "rgba(196,79,54,0.05)" }}
      >
        <div className="flex items-center gap-2 mb-2">
          <BellRing size={18} style={{ color: "var(--terracotta)" }} />
          <h2 className="text-lg font-medium">Nudge unverified organizers</h2>
        </div>
        <p className="text-sm" style={{ color: "var(--ink-soft)" }}>
          Send a warm reminder email to every organizer whose account isn&apos;t
          fully verified yet — verified organizers publish events instantly
          and unlock payouts. Throttled to once per organizer every 7 days
          so you can&apos;t accidentally spam them.
        </p>

        <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div
            className="text-sm rounded-md px-4 py-3 flex-1"
            style={{ background: "rgba(255,255,255,0.7)", border: "1px solid rgba(0,0,0,0.06)" }}
            data-testid="admin-verify-reminder-stats"
          >
            {reminderPreview ? (
              <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
                <div>
                  <span
                    className="font-serif text-2xl mr-1"
                    data-testid="admin-verify-reminder-would-send"
                  >
                    {reminderPreview.would_send}
                  </span>
                  <span className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>
                    ready to email
                  </span>
                </div>
                <div>
                  <span className="font-serif text-lg mr-1">
                    {reminderPreview.throttled_recent}
                  </span>
                  <span className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>
                    skipped (recent)
                  </span>
                </div>
                <div>
                  <span className="font-serif text-lg mr-1">
                    {reminderPreview.unverified_total}
                  </span>
                  <span className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>
                    unverified total
                  </span>
                </div>
              </div>
            ) : (
              <span style={{ color: "var(--ink-soft)" }}>Counting eligible organizers…</span>
            )}
          </div>
          <Button
            type="button"
            data-testid="admin-verify-reminder-send"
            onClick={onSendReminders}
            disabled={reminderBusy || !reminderPreview}
            className="btn-primary py-6 px-8 rounded-full inline-flex items-center gap-2 whitespace-nowrap"
          >
            <Send size={16} />
            {reminderBusy ? "Sending…" : "Send verification reminder"}
          </Button>
        </div>
      </div>

      <p className="mt-10 text-xs" style={{ color: "var(--ink-soft)" }}>
        Tip: co-admins are a great fit for a partner who helps you review vendor
        applications, verify organizers, or run analytics — without having to
        share your password.
      </p>
    </div>
  );
}
